"use client";

import React from "react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";

export interface ToggleOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface ToggleProps {
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  name: string;
  value?: string;
  options: [ToggleOption, ToggleOption];
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  required?: boolean;
  className?: string;
}

export function Toggle({
  ref,
  label,
  name,
  value,
  options,
  onChange,
  disabled = false,
  error,
  helperText,
  required,
  className = "",
}: ToggleProps) {
  const [leftOption, rightOption] = options;
  const selectedValue = value || leftOption.value;

  const handleToggle = (newValue: string) => {
    if (disabled) return;
    onChange?.(newValue);
  };

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Typography>
      )}

      <div
        className={cn(
          "relative inline-flex items-center p-1 rounded-xl border-2 transition-all duration-200",
          error ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-100",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        )}
      >
        {/* Hidden input for form submission */}
        <input
          ref={ref}
          type="hidden"
          name={name}
          value={selectedValue}
          required={required}
        />

        {/* Left Option */}
        <button
          type="button"
          onClick={() => handleToggle(leftOption.value)}
          disabled={disabled}
          className={cn(
            "relative z-10 flex items-center gap-2 px-6 py-2.5 rounded-lg",
            "text-sm font-medium transition-all duration-200",
            selectedValue === leftOption.value
              ? "text-white bg-navigator-primary shadow-md"
              : "text-gray-600 hover:text-gray-900",
            "disabled:cursor-not-allowed"
          )}
        >
          {leftOption.icon && (
            <span className="w-4 h-4">{leftOption.icon}</span>
          )}
          {leftOption.label}
        </button>

        {/* Right Option */}
        <button
          type="button"
          onClick={() => handleToggle(rightOption.value)}
          disabled={disabled}
          className={cn(
            "relative z-10 flex items-center gap-2 px-6 py-2.5 rounded-lg",
            "text-sm font-medium transition-all duration-200",
            selectedValue === rightOption.value
              ? "text-white bg-navigator-primary shadow-md"
              : "text-gray-600 hover:text-gray-900",
            "disabled:cursor-not-allowed"
          )}
        >
          {rightOption.icon && (
            <span className="w-4 h-4">{rightOption.icon}</span>
          )}
          {rightOption.label}
        </button>
      </div>

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}
      {helperText && !error && (
        <Typography as="p" variant="body" color="light" className="mt-1.5">{helperText}</Typography>
      )}
    </div>
  );
}
